import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from "react";
import { authAPI } from "../services/api";

const AuthContext = createContext();

const IDLE_TIMEOUT_MS  = 30 * 60 * 1000; // 30 minutes
const IDLE_CHECK_MS    = 60 * 1000;       // check every 1 minute

const decodeJWT = (token) => {
  try {
    return JSON.parse(atob(token.split(".")[1]));
  } catch {
    return null;
  }
};

const isTokenValid = (token) => {
  const payload = decodeJWT(token);
  if (!payload || !payload.exp) return false;
  return payload.exp * 1000 > Date.now();
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within an AuthProvider");
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user,    setUser]    = useState(null);
  const [token,   setToken]   = useState(null);
  const [loading, setLoading] = useState(true);

  const lastActivityRef = useRef(Date.now());

  // ── Restore session on mount, validate JWT expiry ──────────────────
  useEffect(() => {
    const storedToken = localStorage.getItem("token");
    const storedUser  = localStorage.getItem("user");

    if (storedToken && storedUser && isTokenValid(storedToken)) {
      setToken(storedToken);
      setUser(JSON.parse(storedUser));
    } else {
      // Clear any stale/expired data
      localStorage.removeItem("token");
      localStorage.removeItem("user");
    }
    setLoading(false);
  }, []);

  // ── Track user activity ────────────────────────────────────────────
  useEffect(() => {
    const update = () => { lastActivityRef.current = Date.now(); };
    const events = ["mousedown", "mousemove", "keydown", "scroll", "touchstart", "click"];
    events.forEach(ev => window.addEventListener(ev, update, { passive: true }));
    return () => events.forEach(ev => window.removeEventListener(ev, update));
  }, []);

  // ── Idle timeout: auto-logout after 30 min of inactivity ──────────
  const doLogout = useCallback(() => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setToken(null);
    setUser(null);
  }, []);

  useEffect(() => {
    if (!user) return;
    const timer = setInterval(() => {
      // Also re-validate token expiry on each check
      const stored = localStorage.getItem("token");
      if (!stored || !isTokenValid(stored)) {
        doLogout();
        return;
      }
      if (Date.now() - lastActivityRef.current > IDLE_TIMEOUT_MS) {
        doLogout();
      }
    }, IDLE_CHECK_MS);
    return () => clearInterval(timer);
  }, [user, doLogout]);

  // ── Auth actions ───────────────────────────────────────────────────
  const login = async (credentials) => {
    try {
      const response = await authAPI.login({
        username: credentials.username.trim(),
        password: credentials.password,
      });
      const { token: newToken, user: newUser } = response.data;

      localStorage.setItem("token", newToken);
      localStorage.setItem("user", JSON.stringify(newUser));
      lastActivityRef.current = Date.now();

      setToken(newToken);
      setUser(newUser);

      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || "Login failed. Please try again.";
      return { success: false, message };
    }
  };

  const logout = doLogout;

  const isAdmin = user?.role === "admin";

  const value = {
    user,
    token,
    login,
    logout,
    isAdmin,
    isAuthenticated: !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
